import { groupBy } from 'lodash-es';
import { catchError, map } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Signal, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';

import { ApmTranslatePipe, AuthFacade } from '@mitsubishi/core';
import { MiscDataService, SubfamilyData } from '@mitsubishi/shared/data-access';
import { ButtonComponent, ResourceImageDirective } from '@mitsubishi/shared/ui';
import { WindowedSpinnerComponent } from '@mitsubishi/ui/spinner';

export interface ModelSearchEntityDetailsDialogData {
  modelName: string;
  modelId: number;
  subfamilyId: number;
}

@Component({
  selector: 'apm-model-info-dialog',
  templateUrl: './model-info-dialog.component.html',
  styleUrls: ['./model-info-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatTabsModule,
    ResourceImageDirective,
    WindowedSpinnerComponent,
    ApmTranslatePipe,
    ButtonComponent,
  ],
})
export class ModelInfoDialogComponent {
  dialogData = inject(MAT_DIALOG_DATA) as ModelSearchEntityDetailsDialogData;
  dialogRef = inject(MatDialogRef<ModelInfoDialogComponent>);
  #authFacade = inject(AuthFacade);
  #miscDataService = inject(MiscDataService);

  readonly hasDebugPermission = this.#authFacade.canDebug;
  readonly isLoading = signal(true);

  readonly entityDetails: Signal<Record<number, SubfamilyData[]>> = toSignal(
    this.#miscDataService.getModelSubfamilyInfo(this.dialogData.modelId, this.dialogData.subfamilyId).pipe(
      map(response => {
        this.isLoading.set(false);
        return groupBy(response, 'propertyId');
      }),
      catchError(() => {
        this.isLoading.set(false);
        return [];
      })
    ),
    { initialValue: {} as Record<number, SubfamilyData[]> }
  );

  getMainContentClasses(hasImages: boolean): string {
    return hasImages ? 'col-lg-8 mt-4' : 'col-lg-12 mt-2';
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
